import type { BookDetail, BookReadingTime, ComprehensionStatus, CuratedReaderSupportStatus, DictionaryEntry, GrammarExplanation, ImportState, KanjiDetail, LibraryBook, NormalizedRegionGeometry, OcrRegion, PageReadingTimeEntry, PageReadingTimeSummary, ReadingGoalPeriod, ReadingGoals, ReadingGoalsDashboard, ReadingTimeMonthSummary, ReadingTimeSegment, ReadingTimeSummary, TextRegionSuggestion, TranslationFeedbackBatch, TranslationFeedbackDetails, TranslationFinding, TranslationFindingInbox, TranslationIndexStatus, TranslationResult, VocabularyWord, VolumeSynopsis, VolumeVocabularyAnalysis, VolumeVocabularyEntry, VolumeVocabularyExclusion } from "./types";

export class ReadingTimeRequestError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = "ReadingTimeRequestError";
    this.status = status;
  }
}

async function apiRequest<T>(input: RequestInfo | URL, init?: RequestInit): Promise<T> {
  const response = await fetch(input, init);
  if (!response.ok) {
    const payload = (await response.json().catch(() => null)) as { error?: string } | null;
    throw new Error(payload?.error ?? `Request failed (${response.status})`);
  }
  return response.json() as Promise<T>;
}

export async function requestJapaneseSpeech(input: {
  text: string;
  reading?: string;
  mode: "sentence" | "word";
}, signal?: AbortSignal): Promise<Blob> {
  const response = await fetch("/api/speech", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
    signal
  });
  if (!response.ok) {
    const payload = (await response.json().catch(() => null)) as { error?: string } | null;
    throw new Error(payload?.error ?? "Could not generate Japanese speech");
  }
  return response.blob();
}

export type AuthStatus = { configured: boolean; authenticated: boolean; username: string | null };

export async function loadAuthStatus(signal?: AbortSignal): Promise<AuthStatus> {
  return apiRequest("/api/auth/status", { signal });
}

export async function startAuthSetup(username: string, password: string): Promise<{ qrCode: string; secret: string }> {
  return apiRequest("/api/auth/setup", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, password })
  });
}

export async function confirmAuthSetup(code: string): Promise<AuthStatus> {
  return apiRequest("/api/auth/setup/confirm", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ code })
  });
}

export async function login(username: string, password: string, code: string): Promise<AuthStatus> {
  return apiRequest("/api/auth/login", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, password, code })
  });
}

export async function logout(): Promise<void> {
  const response = await fetch("/api/auth/logout", { method: "POST" });
  if (!response.ok) throw new Error("Could not sign out");
}

export async function loadLibrary(signal?: AbortSignal): Promise<{ books: LibraryBook[]; import: ImportState }> {
  return apiRequest("/api/library", { signal });
}

export async function loadBook(bookId: string, signal?: AbortSignal): Promise<BookDetail> {
  const payload = await apiRequest<{ book: BookDetail }>(`/api/books/${bookId}`, { signal });
  return payload.book;
}

export async function loadVolumeVocabularyAnalysis(bookId: string, signal?: AbortSignal): Promise<VolumeVocabularyAnalysis> {
  const payload = await apiRequest<{ analysis: VolumeVocabularyAnalysis }>(`/api/books/${bookId}/vocabulary-analysis`, { signal });
  return payload.analysis;
}

const volumeSynopsisCache = new Map<string, VolumeSynopsis>();

export function getCachedVolumeSynopsis(bookId: string): VolumeSynopsis | null {
  const cached = volumeSynopsisCache.get(bookId) ?? null;
  if (!cached) return null;
  const expiresAt = Date.parse(cached.expiresAt ?? "");
  if (!Number.isFinite(expiresAt) || expiresAt <= Date.now()) {
    volumeSynopsisCache.delete(bookId);
    return null;
  }
  return cached;
}

export async function loadVolumeSynopsis(bookId: string, signal?: AbortSignal): Promise<VolumeSynopsis> {
  signal?.throwIfAborted();
  const cached = getCachedVolumeSynopsis(bookId);
  if (cached) return cached;
  const payload = await apiRequest<{ synopsis: VolumeSynopsis }>(`/api/books/${bookId}/synopsis`, { signal });
  if (Date.parse(payload.synopsis.expiresAt ?? "") > Date.now()) {
    volumeSynopsisCache.set(bookId, payload.synopsis);
  }
  return payload.synopsis;
}

export async function loadBookReadingTime(bookId: string, signal?: AbortSignal): Promise<BookReadingTime> {
  const payload = await apiRequest<{ readingTime: BookReadingTime }>(`/api/books/${bookId}/reading-time`, { signal });
  return payload.readingTime;
}

export async function loadPageReadingTime(
  bookId: string,
  currentPageId?: string,
  signal?: AbortSignal
): Promise<PageReadingTimeSummary> {
  const parameters = new URLSearchParams();
  if (currentPageId) parameters.set("currentPageId", currentPageId);
  const suffix = parameters.size > 0 ? `?${parameters}` : "";
  const payload = await apiRequest<{ summary: PageReadingTimeSummary }>(`/api/books/${bookId}/page-reading-time${suffix}`, { signal });
  return payload.summary;
}

export async function loadSlowPageReview(signal?: AbortSignal): Promise<PageReadingTimeEntry[]> {
  const payload = await apiRequest<{ items: PageReadingTimeEntry[] }>("/api/review/slow-pages", { signal });
  return payload.items;
}

export async function loadReadingTimeSummary(from: string, to: string, signal?: AbortSignal): Promise<ReadingTimeSummary> {
  const parameters = new URLSearchParams({ from, to });
  const payload = await apiRequest<{ summary: ReadingTimeSummary }>(`/api/reading-time/summary?${parameters}`, { signal });
  return payload.summary;
}

export async function loadReadingTimeMonthly(from: string, to: string, signal?: AbortSignal): Promise<ReadingTimeMonthSummary> {
  const parameters = new URLSearchParams({
    from,
    to,
    tzOffsetMinutes: String(new Date().getTimezoneOffset())
  });
  const payload = await apiRequest<{ summary: ReadingTimeMonthSummary }>(`/api/reading-time/monthly?${parameters}`, { signal });
  return payload.summary;
}

export async function loadReadingGoalsDashboard(
  day: Pick<ReadingGoalPeriod, "from" | "to">,
  week: Pick<ReadingGoalPeriod, "from" | "to">,
  signal?: AbortSignal
): Promise<ReadingGoalsDashboard> {
  const parameters = new URLSearchParams({
    dayFrom: day.from,
    dayTo: day.to,
    weekFrom: week.from,
    weekTo: week.to
  });
  return apiRequest<ReadingGoalsDashboard>(`/api/reading-time/goals?${parameters}`, { signal });
}

export async function saveReadingGoals(input: Pick<ReadingGoals, "dailyMinutes" | "weeklyMinutes">): Promise<ReadingGoals> {
  const payload = await apiRequest<{ goals: ReadingGoals }>("/api/reading-time/goals", {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input)
  });
  return payload.goals;
}

export async function recordReadingTime(
  bookId: string,
  segment: ReadingTimeSegment,
  options: { signal?: AbortSignal; keepalive?: boolean } = {}
): Promise<boolean> {
  const response = await fetch(`/api/books/${bookId}/reading-time`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(segment),
    signal: options.signal,
    keepalive: options.keepalive ?? false
  });
  if (!response.ok) {
    const payload = (await response.json().catch(() => null)) as { error?: string } | null;
    throw new ReadingTimeRequestError(
      payload?.error ?? `Request failed (${response.status})`,
      response.status,
    );
  }
  const payload = await response.json() as { recorded?: boolean };
  return payload.recorded === true;
}

export async function excludeVolumeVocabularyWord(bookId: string, word: VolumeVocabularyEntry): Promise<VolumeVocabularyExclusion> {
  const payload = await apiRequest<{ exclusion: VolumeVocabularyExclusion }>(`/api/books/${bookId}/vocabulary-exclusions`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ expression: word.expression, lemma: word.lemma, reading: word.reading })
  });
  return payload.exclusion;
}

export async function restoreVolumeVocabularyWord(
  bookId: string,
  word: Pick<VolumeVocabularyExclusion, "expression" | "lemma" | "reading">
): Promise<void> {
  const response = await fetch(`/api/books/${bookId}/vocabulary-exclusions`, {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ expression: word.expression, lemma: word.lemma, reading: word.reading })
  });
  if (!response.ok) {
    const payload = (await response.json().catch(() => null)) as { error?: string } | null;
    throw new Error(payload?.error ?? `Request failed (${response.status})`);
  }
}

export async function retryVolumeOcrIndex(bookId: string): Promise<void> {
  await apiRequest(`/api/books/${bookId}/ocr-index/retry`, { method: "POST" });
}

export async function retryVolumeSpeechIndex(bookId: string): Promise<void> {
  await apiRequest(`/api/books/${bookId}/tts-index/retry`, { method: "POST" });
}

export async function loadVolumeCuratedSupport(bookId: string, signal?: AbortSignal): Promise<CuratedReaderSupportStatus> {
  const payload = await apiRequest<{ support: CuratedReaderSupportStatus }>(`/api/books/${bookId}/curated-support`, { signal });
  return payload.support;
}

export async function loadVolumeTranslationIndex(bookId: string, signal?: AbortSignal): Promise<TranslationIndexStatus> {
  const payload = await apiRequest<{ index: TranslationIndexStatus }>(`/api/books/${bookId}/translation-index`, { signal });
  return payload.index;
}

export async function prepareVolumeTranslations(
  bookId: string,
  scope: "outdated" | "all" = "outdated"
): Promise<TranslationIndexStatus> {
  const payload = await apiRequest<{ index: TranslationIndexStatus }>(`/api/books/${bookId}/translation-index`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ scope })
  });
  return payload.index;
}

export async function retryVolumeTranslationIndex(bookId: string): Promise<TranslationIndexStatus> {
  const payload = await apiRequest<{ index: TranslationIndexStatus }>(`/api/books/${bookId}/translation-index/retry`, { method: "POST" });
  return payload.index;
}

export async function saveProgress(bookId: string, currentPage: number, completed = false): Promise<void> {
  await apiRequest(`/api/books/${bookId}/progress`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ currentPage, completed }),
    keepalive: true
  });
}

export async function uploadArchive(file: File): Promise<void> {
  const body = new FormData();
  body.append("archive", file);
  await apiRequest("/api/import", { method: "POST", body });
}

export async function loadOcrStatus(): Promise<{ available: boolean; ready: boolean; message: string }> {
  return apiRequest("/api/ocr/status");
}

export interface OcrRegionsPayload {
  regions: OcrRegion[];
  refreshingPageIds: string[];
}

export async function loadOcrRegions(pageIds: string[], signal?: AbortSignal): Promise<OcrRegionsPayload> {
  if (pageIds.length === 0) return { regions: [], refreshingPageIds: [] };
  const query = new URLSearchParams();
  for (const pageId of pageIds) query.append("pageId", pageId);
  return apiRequest<OcrRegionsPayload>(`/api/ocr/regions?${query}`, { signal });
}

export async function loadTextRegionSuggestions(pageId: string, signal?: AbortSignal): Promise<TextRegionSuggestion[]> {
  const payload = await apiRequest<{ regions: TextRegionSuggestion[] }>(`/api/pages/${pageId}/text-regions`, { signal });
  return payload.regions;
}

export async function deleteOcrRegion(regionId: string): Promise<void> {
  const response = await fetch(`/api/ocr/regions/${regionId}`, { method: "DELETE" });
  if (!response.ok && response.status !== 404) {
    const payload = (await response.json().catch(() => null)) as { error?: string } | null;
    throw new Error(payload?.error ?? "Could not remove the selection");
  }
}

export async function correctOcrRegion(regionId: string, text: string): Promise<OcrRegion> {
  const payload = await apiRequest<{ region: OcrRegion }>(`/api/ocr/regions/${regionId}/text`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ text })
  });
  return payload.region;
}

const comprehensionWrites = new Map<string, Promise<void>>();

export async function setOcrRegionComprehension(
  regionId: string,
  status: ComprehensionStatus | null
): Promise<OcrRegion> {
  const previousWrite = comprehensionWrites.get(regionId)?.catch(() => undefined) ?? Promise.resolve();
  const write = previousWrite.then(async () => {
    const payload = await apiRequest<{ region: OcrRegion }>(`/api/ocr/regions/${regionId}/comprehension`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status })
    });
    return payload.region;
  });
  const settled = write.then(() => undefined, () => undefined);
  comprehensionWrites.set(regionId, settled);
  settled.finally(() => {
    if (comprehensionWrites.get(regionId) === settled) comprehensionWrites.delete(regionId);
  });
  return write;
}

export async function recognizePageRegion(
  pageId: string,
  region: NormalizedRegionGeometry,
  replaceRegionId?: string
): Promise<OcrRegion> {
  const payload = await apiRequest<{ region: OcrRegion }>(`/api/pages/${pageId}/ocr-region`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ...region, replaceRegionId })
  });
  return payload.region;
}

export async function lookupDictionary(term: string, partOfSpeech: string[] = [], signal?: AbortSignal, regionId?: string, surface?: string): Promise<DictionaryEntry[]> {
  const query = new URLSearchParams({ term });
  if (regionId) query.set("regionId", regionId);
  if (surface) query.set("surface", surface);
  for (const category of partOfSpeech.filter((value) => value && value !== "*").slice(0, 8)) query.append("pos", category);
  const payload = await apiRequest<{ entries: DictionaryEntry[] }>(`/api/dictionary?${query}`, { signal });
  return payload.entries;
}

export async function lookupCompoundWords(terms: string[], signal?: AbortSignal, regionId?: string): Promise<string[]> {
  if (terms.length === 0) return [];
  const payload = await apiRequest<{ matches: string[] }>("/api/dictionary/compounds", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ terms, regionId }),
    signal
  });
  return payload.matches;
}

export async function loadKanjiDetail(literal: string, signal?: AbortSignal): Promise<KanjiDetail | null> {
  const payload = await apiRequest<{ kanji: KanjiDetail | null }>(`/api/kanji/${encodeURIComponent(literal)}`, { signal });
  return payload.kanji;
}

export async function loadStoredTranslation(regionId: string, signal?: AbortSignal): Promise<TranslationResult | null> {
  const payload = await apiRequest<{ translation: TranslationResult | null }>(`/api/ocr/regions/${regionId}/translation`, { signal });
  return payload.translation;
}

export async function loadStoredTranslations(regionId: string, signal?: AbortSignal): Promise<TranslationResult[]> {
  const payload = await apiRequest<{ translation: TranslationResult | null; translations?: TranslationResult[] }>(
    `/api/ocr/regions/${regionId}/translation`,
    { signal }
  );
  return payload.translations ?? (payload.translation ? [payload.translation] : []);
}

export async function loadStoredGrammarExplanation(regionId: string, signal?: AbortSignal): Promise<GrammarExplanation | null> {
  const payload = await apiRequest<{ explanation: GrammarExplanation | null }>(`/api/ocr/regions/${regionId}/explanation`, { signal });
  return payload.explanation;
}

export async function explainJapanese(regionId: string): Promise<GrammarExplanation> {
  return apiRequest("/api/explain", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ regionId })
  });
}

export async function savePersonalDictionary(input: {
  expression: string;
  reading?: string;
  definition: string;
  scope: "series" | "global";
  regionId: string;
}): Promise<{ id: string }> {
  const payload = await apiRequest<{ entry: { id: string } }>("/api/personal-dictionary", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input)
  });
  return payload.entry;
}

export async function updatePersonalDictionary(entryId: string, input: { definition: string; reading?: string }): Promise<void> {
  await apiRequest(`/api/personal-dictionary/${entryId}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input)
  });
}

export async function deletePersonalDictionary(entryId: string): Promise<void> {
  const response = await fetch(`/api/personal-dictionary/${entryId}`, { method: "DELETE" });
  if (!response.ok && response.status !== 404) {
    const payload = (await response.json().catch(() => null)) as { error?: string } | null;
    throw new Error(payload?.error ?? "Could not remove this personal meaning");
  }
}

export async function loadRegionNote(regionId: string, signal?: AbortSignal): Promise<string> {
  const payload = await apiRequest<{ note: string | null }>(`/api/ocr/regions/${regionId}/note`, { signal });
  return payload.note ?? "";
}

export async function saveRegionNote(regionId: string, note: string): Promise<string> {
  const payload = await apiRequest<{ note: string | null }>(`/api/ocr/regions/${regionId}/note`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ note })
  });
  return payload.note ?? "";
}

export async function translateJapanese(text: string, regionId: string, context?: string): Promise<TranslationResult> {
  return apiRequest("/api/translate", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ text, regionId, context })
  });
}

export async function saveTranslationFeedback(
  regionId: string,
  rating: "good" | "bad",
  translation: string,
  model: string,
  details: { note?: string | null; suggestedTranslation?: string | null } = {}
): Promise<TranslationFeedbackDetails> {
  return apiRequest<TranslationFeedbackDetails>(`/api/ocr/regions/${regionId}/translation/feedback`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ rating, translation, model, ...details })
  });
}

export async function loadTranslationFindings(): Promise<TranslationFindingInbox> {
  return apiRequest("/api/translation-feedback/findings");
}

export async function updateTranslationFinding(
  id: number,
  input: { status: "captured" | "approved" | "rejected"; approvedTranslation?: string | null; reviewerNote?: string | null; categories?: string[] }
): Promise<TranslationFinding> {
  return apiRequest(`/api/translation-feedback/findings/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input)
  });
}

export async function loadTranslationFeedbackBatches(): Promise<TranslationFeedbackBatch[]> {
  return apiRequest("/api/translation-feedback/batches");
}

export async function freezeTranslationFeedbackBatch(name?: string): Promise<TranslationFeedbackBatch> {
  return apiRequest("/api/translation-feedback/batches", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(name ? { name } : {})
  });
}

export async function saveVocabulary(input: {
  expression: string;
  reading?: string;
  definition?: string;
  customDefinition?: boolean;
  sentence?: string;
  pageId: string;
  regionId: string;
}): Promise<void> {
  await apiRequest("/api/vocabulary", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input)
  });
}

export async function loadVocabulary(signal?: AbortSignal): Promise<VocabularyWord[]> {
  const payload = await apiRequest<{ words: VocabularyWord[] }>("/api/vocabulary", { signal });
  return payload.words;
}

export async function deleteVocabularyWord(wordId: string): Promise<void> {
  const response = await fetch(`/api/vocabulary/${wordId}`, { method: "DELETE" });
  if (!response.ok && response.status !== 404) {
    const payload = (await response.json().catch(() => null)) as { error?: string } | null;
    throw new Error(payload?.error ?? "Could not remove the saved word");
  }
}
